export type EvidenceItemModel = {
  id: string;
  evidenceType: string;
  status: EvidenceStatus;
  ownerUserId: string;
  numberOfLessons: number | null;
  documentId: string | null;
  fileName: string | null;
  createdAt: string;
  updatedAt: string;
  isReported: boolean;
  aiDecision: string | null;
  aiRejectionReason: string | null;
  modelDecisionBy: string | null;
  humanReviewRequestedAt: string | null;
  createdByUserId: string | null;
  createdByUserName: string | null;
  reportedInInjunctionId: string | null;
  reportedInExternalPeriodId: string | null;
};

export type EvidenceItemListModel = {
  id: string;
  evidenceType: string;
  status: EvidenceStatus;
  ownerUserId: string;
  numberOfLessons: number | null;
  documentId: string | null;
  fileName: string | null;
  createdAt: string;
  updatedAt: string;
  isReported: boolean;
};

export type EvidenceStatus =
  | "draft"
  | "queued_for_analysis"
  | "analyzing"
  | "awaiting_review"
  | "approved"
  | "rejected_by_ai"
  | "rejected_by_human"
  | "failed";

export const ANALYZING_STATUSES = ["queued_for_analysis", "analyzing"] as const;

export const isAnalyzingStatus = (status: EvidenceStatus | string): boolean =>
  ANALYZING_STATUSES.includes(status as (typeof ANALYZING_STATUSES)[number]);

export const EVIDENCE_STATUS_LABELS: Record<EvidenceStatus, string> = {
  draft: "Kladde",
  queued_for_analysis: "Analyseres",
  analyzing: "Analyseres",
  awaiting_review: "Afventer sekretariat",
  approved: "Godkendt",
  rejected_by_ai: "Afvist",
  rejected_by_human: "Afvist",
  failed: "Fejlede",
};

export const normalizeEvidenceStatus = (
  status: unknown,
): EvidenceStatus | undefined => {
  if (typeof status !== "string" || status.trim().length === 0) {
    return undefined;
  }

  const normalizedStatus = status
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/-/g, "_")
    .toLowerCase();

  if (normalizedStatus in EVIDENCE_STATUS_LABELS) {
    return normalizedStatus as EvidenceStatus;
  }

  return undefined;
};

export const getEvidenceStatusLabel = (
  status: EvidenceStatus | string | null | undefined,
): string => {
  const normalizedStatus = normalizeEvidenceStatus(status);

  if (normalizedStatus) {
    return EVIDENCE_STATUS_LABELS[normalizedStatus];
  }

  if (typeof status === "string" && status.trim().length > 0) {
    return status;
  }

  return "Ukendt";
};

export const isApprovedStatus = (status: EvidenceStatus): boolean =>
  status === "approved";

export const isRejectedStatus = (status: EvidenceStatus): boolean =>
  status === "rejected_by_ai" || status === "rejected_by_human";

export const isRejectedByAiStatus = (status: EvidenceStatus): boolean =>
  status === "rejected_by_ai";

export const isAwaitingReviewStatus = (status: EvidenceStatus): boolean =>
  status === "awaiting_review";
